import { FC, useEffect, useState, useMemo } from 'react';
import { SettingsViewProps } from './settings.interfaces';
import { Grid } from '@mui/material';
import { pages } from '../../../utils/common';
import {
  useSettingsTabMenuItems,
  useSettingsAvailableRouteIds,
} from './settings.helpers';
import MainTitle from '../../components/mainTitle';
import TabGroup from '../../components/tabGroup';
import { Helmet } from 'react-helmet';
import { CONSTANTS } from '../../../utils/CONSTS';
import NavigationDrawer from '../../components/navigationDrawer/navigationDrawer';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useRbacState } from 'src/context/rbac.context';
import { useRbacSafeLandingPath } from 'src/hooks/useRbacSafeLandingPath';
import { hasAnySubmoduleReadPermission } from 'src/utils/rbacHelpers';

const SettingsContent: FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const { settingsSubRouteId } = useParams();
  const navigate = useNavigate();
  const { translateText } = useTranslation();
  const { rbac } = useRbacState();
  const safeLanding = useRbacSafeLandingPath();
  const availableRouteIds = useSettingsAvailableRouteIds();
  const tabs = useSettingsTabMenuItems(translateText);

  const hasAccess = useMemo(
    () => hasAnySubmoduleReadPermission(rbac, 'settings'),
    [rbac]
  );

  useEffect(() => {
    if (availableRouteIds.length === 0) return;

    const routeId =
      settingsSubRouteId && settingsSubRouteId !== 'undefined'
        ? settingsSubRouteId
        : availableRouteIds[0];

    if (!availableRouteIds.includes(routeId)) {
      navigate(pages.settings.path + '/' + availableRouteIds[0], {
        replace: true,
      });
      return;
    }

    const tabIndex = availableRouteIds.indexOf(routeId);
    setSelectedTab(tabIndex >= 0 ? tabIndex : 0);

    if (!settingsSubRouteId || settingsSubRouteId === 'undefined') {
      navigate(pages.settings.path + '/' + routeId, { replace: true });
    }
  }, [settingsSubRouteId, navigate, availableRouteIds]);

  if (!hasAccess) {
    return <Navigate to={safeLanding} replace />;
  }

  if (availableRouteIds.length === 0) {
    return <Navigate to={safeLanding} replace />;
  }

  const locationChangeHandler = (value: string | number) => {
    const tabIndex = value as number;
    setSelectedTab(tabIndex);
    if (tabIndex >= 0 && tabIndex < availableRouteIds.length) {
      navigate(pages.settings.path + '/' + availableRouteIds[tabIndex], {
        replace: true,
      });
    }
  };

  return (
    <Grid container sx={{ height: '100%' }} data-testid="settings-view-container">
      <Grid item xs={12}>
        <MainTitle title={translateText('settings.title')} subTitle={selectedTab ? tabs[selectedTab].label : ''} />
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          height: 'calc(100% - 64px)',
          overflow: 'hidden',
          px: { xs: 0, sm: 0 },
        }}
      >
        <TabGroup
          key={selectedTab}
          tabs={tabs}
          initiallySelected={selectedTab}
          onTabChange={(value) => locationChangeHandler(value)}
          dataTestIdPrefix="settings"
        />
      </Grid>
    </Grid>
  );
};

// Main component that provides navigation context
const SettingsView: FC<SettingsViewProps> = () => {
  return (
    <NavigationDrawer
      selectedPage={pages.settings.path}
      window={window ?? null}
    >
      <Helmet>
        <title>{CONSTANTS.title.settings}</title>
      </Helmet>
      <SettingsContent />
    </NavigationDrawer>
  );
};

export default SettingsView;
